import React from 'react';
import { useLocation } from 'react-router-dom';

import { AdminListPageConfig, AdminListPageRenderer, TabDef, useAdminListPage } from 'hooks/useAdminListPage';
import { PERMISSIONS } from 'models/Profile';

type PermissionKey = keyof typeof PERMISSIONS;

export interface TabbedAdminListPageTab<T = any> {
  label: string;
  path: string;
  permission?: PermissionKey;
  config: AdminListPageConfig<T>;
}

function TabPanel({ tab, pageTitle, tabDefs }: { tab: TabbedAdminListPageTab; pageTitle: string; tabDefs: TabDef[] }) {
  const state = useAdminListPage(tab.config);

  return (
    <AdminListPageRenderer
      config={tab.config}
      state={state}
      tabs={{ pageTitle, tabDefs, currentTabLabel: tab.label }}
    />
  );
}

export default function TabbedAdminListPage({
  pageTitle,
  tabs,
}: {
  pageTitle: string;
  tabs: TabbedAdminListPageTab[];
}) {
  const location = useLocation();

  const tabDefs: TabDef[] = tabs.map((t) => ({
    label: t.label,
    path: t.path,
    ...(t.permission ? { permission: t.permission } : {}),
  }));

  // Prefix match, not equality: a tab whose slideout deep-links appends a resource id to its own
  // path, and that must still resolve to the tab rather than falling through to the first one.
  // An exact match wins outright, and among prefixes the longest one wins — so a tab nested under
  // another tab's path (Attributes / Values) resolves to itself regardless of declaration order.
  const activeTab =
    tabs.find((t) => location.pathname === t.path) ??
    [...tabs].sort((a, b) => b.path.length - a.path.length).find((t) => location.pathname.startsWith(`${t.path}/`)) ??
    tabs[0];

  return <TabPanel key={activeTab.path} tab={activeTab} pageTitle={pageTitle} tabDefs={tabDefs} />;
}
